import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Palmtree, Loader2, X, Trash2, Plus, ClipboardList, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};
const daysBetween = (s, e) => {
  if (!s || !e) return 0;
  return Math.max(0, Math.round((new Date(e) - new Date(s)) / 86400000) + 1);
};
const entitlementOf = (hire) => {
  if (!hire) return { years: null, days: 0 };
  const start = new Date(hire);
  if (isNaN(start)) return { years: null, days: 0 };
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  const anniv = new Date(start);
  anniv.setFullYear(start.getFullYear() + years);
  if (anniv > now) years -= 1;
  const days = years < 1 ? 0 : years < 5 ? 14 : years < 15 ? 20 : 26;
  return { years, days };
};

export default function YillikIzin() {
  const qc = useQueryClient();
  const [recordFor, setRecordFor] = useState(null);
  const [recForm, setRecForm] = useState({ start_date: todayISO(), end_date: todayISO(), description: "" });

  const { data: personnel = [], isLoading } = useQuery({
    queryKey: ["personnel"],
    queryFn: async () => (await api.get("/personnel")).data,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => (await api.get("/branches")).data,
  });
  const { data: allLeaves = [] } = useQuery({
    queryKey: ["leaves"],
    queryFn: async () => (await api.get("/leaves")).data,
  });
  const { data: records = [], isLoading: recLoading } = useQuery({
    queryKey: ["leaves", recordFor?.id],
    queryFn: async () => (await api.get(`/leaves?personnel_id=${recordFor.id}`)).data,
    enabled: !!recordFor,
  });

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const year = String(new Date().getFullYear());
  const usedOf = (pid) => allLeaves.filter((l) => l.personnel_id === pid && (l.start_date || "").startsWith(year)).reduce((s, l) => s + (l.days || 0), 0);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["leaves"] });
  };

  const addRecM = useMutation({
    mutationFn: (payload) => api.post("/leaves", payload),
    onSuccess: () => {
      invalidate();
      toast.success("İzin kaydı eklendi");
      setRecForm({ start_date: todayISO(), end_date: todayISO(), description: "" });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Kayıt eklenemedi"),
  });

  const delRecM = useMutation({
    mutationFn: (id) => api.delete(`/leaves/${id}`),
    onSuccess: () => { invalidate(); toast.success("Kayıt silindi"); },
    onError: () => toast.error("Kayıt silinemedi"),
  });

  const openRecords = (p) => {
    setRecordFor(p);
    setRecForm({ start_date: todayISO(), end_date: todayISO(), description: "" });
  };

  const addRecord = (e) => {
    e.preventDefault();
    if (!recForm.start_date || !recForm.end_date) return toast.error("Tarih aralığı seçin");
    if (recForm.end_date < recForm.start_date) return toast.error("Bitiş tarihi başlangıçtan önce olamaz");
    addRecM.mutate({
      personnel_id: recordFor.id,
      start_date: recForm.start_date,
      end_date: recForm.end_date,
      description: recForm.description,
    });
  };

  const rows = personnel.map((p) => {
    const ent = entitlementOf(p.hire_date);
    const used = usedOf(p.id);
    return { p, ent, used, remaining: Math.max(0, ent.days - used) };
  });
  const totalEnt = rows.reduce((s, r) => s + r.ent.days, 0);
  const totalUsed = rows.reduce((s, r) => s + r.used, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <Palmtree className="w-8 h-8 text-emerald-400" /> Yıllık İzin
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          İzin hakkı kıdeme göre otomatik hesaplanır (1-5 yıl: 14, 5-15 yıl: 20, 15+ yıl: 26 gün). Yıllık izin ücretlidir; maaştan kesinti yapılmaz.
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400">Toplam Hak Edilen ({year})</div>
          <div className="text-2xl font-heading font-bold text-white tabular mt-1" data-testid="total-entitlement">{totalEnt} gün</div>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400">Kullanılan</div>
          <div className="text-2xl font-heading font-bold text-amber-400 tabular mt-1" data-testid="total-used">{totalUsed} gün</div>
        </div>
        <div className="glass rounded-2xl p-5 hidden sm:block">
          <div className="text-xs uppercase tracking-widest text-slate-400">Kalan</div>
          <div className="text-2xl font-heading font-bold text-emerald-400 tabular mt-1" data-testid="total-remaining">{Math.max(0, totalEnt - totalUsed)} gün</div>
        </div>
      </div>

      {/* List */}
      <div className="glass rounded-2xl overflow-hidden" data-testid="leave-list">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
          <div className="col-span-3">Personel</div>
          <div className="col-span-2">İşe Giriş / Kıdem</div>
          <div className="col-span-2 text-center">Hak Edilen</div>
          <div className="col-span-2 text-center">Kullanılan</div>
          <div className="col-span-3 text-right">Kalan</div>
        </div>
        <div className="divide-y divide-slate-800/60">
          {rows.map(({ p, ent, used, remaining }) => {
            const pct = ent.days > 0 ? Math.min(100, Math.round((used / ent.days) * 100)) : 0;
            return (
              <div key={p.id} data-testid={`leave-row-${p.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center px-5 py-4">
                <div className="md:col-span-3">
                  <div className="text-sm font-semibold text-white">{p.name}</div>
                  <div className="text-xs text-slate-400">{p.role || "—"} · {branchName(p.branch_id)}</div>
                </div>
                <div className="md:col-span-2 text-sm text-slate-300">
                  <div className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-slate-500" /> {p.hire_date ? fmtDate(p.hire_date) : <span className="text-slate-600">Tarih girilmemiş</span>}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{ent.years !== null ? `${ent.years} yıl kıdem` : "—"}</div>
                </div>
                <div className="md:col-span-2 md:text-center text-sm text-white font-semibold tabular">{ent.days} gün</div>
                <div className="md:col-span-2 md:text-center text-sm text-amber-400 font-semibold tabular">{used} gün</div>
                <div className="md:col-span-3 flex items-center md:justify-end gap-3">
                  <div className="flex-1 md:max-w-[120px]">
                    <div className="flex justify-between text-xs mb-1">
                      <span className={remaining > 0 ? "text-emerald-400 font-semibold" : "text-rose-400 font-semibold"}>{remaining} gün kaldı</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <button
                    data-testid={`leave-records-${p.id}`}
                    onClick={() => openRecords(p)}
                    title="İzin kayıtları"
                    className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 hover:border-emerald-400/50 rounded-lg px-2 py-2 transition-colors"
                  >
                    <ClipboardList className="w-3.5 h-3.5" /> Kayıtlar
                  </button>
                </div>
              </div>
            );
          })}
          {personnel.length === 0 && !isLoading && (
            <div className="text-slate-500 text-sm py-16 text-center flex flex-col items-center gap-2">
              <Palmtree className="w-6 h-6" /> Personel bulunmuyor.
            </div>
          )}
        </div>
      </div>

      {/* Records Modal */}
      {recordFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setRecordFor(null)}>
          <div
            data-testid="leave-records-modal"
            className="glass rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-1">
              <div>
                <h2 className="font-heading text-xl font-bold text-white">{recordFor.name}</h2>
                <p className="text-xs text-slate-400 mt-0.5">Yıllık izin kayıtları — başlangıç / bitiş tarihi</p>
              </div>
              <button data-testid="leave-modal-close" onClick={() => setRecordFor(null)} className="text-slate-500 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={addRecord} className="mt-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-3">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Başlangıç</label>
                <input
                  data-testid="leave-start-input"
                  type="date"
                  required
                  value={recForm.start_date}
                  onChange={(e) => setRecForm((f) => ({ ...f, start_date: e.target.value }))}
                  className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-emerald-500 [color-scheme:dark]"
                />
              </div>
              <div className="sm:col-span-3">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Bitiş</label>
                <input
                  data-testid="leave-end-input"
                  type="date"
                  required
                  value={recForm.end_date}
                  onChange={(e) => setRecForm((f) => ({ ...f, end_date: e.target.value }))}
                  className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-emerald-500 [color-scheme:dark]"
                />
              </div>
              <div className="sm:col-span-4">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Açıklama</label>
                <input
                  data-testid="leave-desc-input"
                  type="text"
                  placeholder="Örn. Yaz tatili"
                  value={recForm.description}
                  onChange={(e) => setRecForm((f) => ({ ...f, description: e.target.value }))}
                  className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-emerald-500"
                />
              </div>
              <button
                data-testid="leave-add-button"
                type="submit"
                disabled={addRecM.isPending}
                className="sm:col-span-2 inline-flex items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2.5 rounded-lg transition-colors"
              >
                {addRecM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Ekle
              </button>
            </form>
            {daysBetween(recForm.start_date, recForm.end_date) > 0 && recForm.end_date >= recForm.start_date && (
              <div className="mt-2 text-xs text-emerald-400" data-testid="leave-days-preview">
                Seçilen aralık: {daysBetween(recForm.start_date, recForm.end_date)} gün
              </div>
            )}

            <div className="mt-5 divide-y divide-slate-800/60" data-testid="leave-records-list">
              {recLoading && <div className="py-8 text-center text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
              {!recLoading && records.length === 0 && (
                <div className="py-8 text-center text-slate-500 text-sm">Henüz izin kaydı yok.</div>
              )}
              {records.map((r) => (
                <div key={r.id} data-testid={`leave-record-${r.id}`} className="flex items-center gap-3 py-3">
                  <div className="shrink-0 text-sm text-white tabular font-semibold">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</div>
                  <div className="w-14 shrink-0 text-xs text-emerald-400 tabular">{r.days} gün</div>
                  <div className="flex-1 text-sm text-slate-300">{r.description || <span className="text-slate-600">Açıklama yok</span>}</div>
                  <button
                    data-testid={`leave-record-delete-${r.id}`}
                    onClick={() => delRecM.mutate(r.id)}
                    disabled={delRecM.isPending}
                    className="text-slate-600 hover:text-rose-400 p-1 transition-colors"
                    title="Kaydı sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
